import { getUserId } from "@/services/auth";
import { getFrontendFirestore } from "@/utils/firebase-helper";
import {
  CurriculumServiceFactory,
  LectureServiceFactory,
  ProgressionServiceFactory,
  TopicServiceFactory,
} from "@shared";
import { wrapService } from "./ServiceWrapper";

const useMockData = process.env.VUE_APP_USE_MOCK_DATA === "true";
const frontendFirestore = getFrontendFirestore();

const unwrappedTopicsService = useMockData
  ? TopicServiceFactory.createLocal()
  : TopicServiceFactory.createFirestore(frontendFirestore);

const unwrappedCurriculumService = useMockData
  ? CurriculumServiceFactory.createLocal()
  : CurriculumServiceFactory.createFirestore(frontendFirestore);

const unwrappedLectureService = useMockData
  ? LectureServiceFactory.createLocal()
  : LectureServiceFactory.createFirestore(frontendFirestore);

const unwrappedProgressionService = useMockData
  ? ProgressionServiceFactory.createLocal()
  : ProgressionServiceFactory.createFirestore(frontendFirestore);

export const TopicsService = wrapService(unwrappedTopicsService);
export const CurriculumService = wrapService(unwrappedCurriculumService);
export const LectureService = wrapService(unwrappedLectureService);
export const ProgressionService = wrapService(unwrappedProgressionService);

/*
 * Mock data setup
 */

export const MOCK_TOPICS = new Map([
  [
    "the-greek-gift-sacrifice-part-1.pgn",
    {
      title: "The Greek Gift Sacrifice Part 1",
      description: "Learn the famous bishop sacrifice on h7/h2",
      isPublic: true,
      ownerId: null,
      tags: ["sacrifice", "bishop", "attack", "greek gift"],
      urls: ["https://en.wikipedia.org/wiki/Greek_gift_sacrifice"],
      workbookRef: "workbook-123",
      difficultyLevel: "Intermediate",
      status: "Published",
    },
  ],
  [
    "basic-tactics-double-check.pgn",
    {
      title: "Basic Tactics: Double Check",
      description: "Master the powerful double check tactic",
      isPublic: false,
      ownerId: getUserId(),
      tags: ["tactics", "check", "double check", "king attack"],
      urls: ["https://www.chess.com/terms/double-check-chess"],
      workbookRef: "workbook-456",
      difficultyLevel: "Beginner",
      status: "Published",
    },
  ],
  [
    "basic-tactics-windmill.pgn",
    {
      title: "Basic Tactics: Windmill",
      description: "The devastating windmill tactic",
      isPublic: true,
      ownerId: null,
      tags: ["tactics", "windmill", "advanced", "rook"],
      urls: ["https://www.chess.com/terms/windmill-chess"],
      workbookRef: "workbook-789",
      difficultyLevel: "Advanced",
      status: "Published",
    },
  ],
  [
    "discover-check-basics.pgn",
    {
      title: "Discover Check Basics",
      description: "Introduction to discovered checks",
      isPublic: true,
      ownerId: null,
      tags: ["tactics", "discovered check", "beginner"],
      urls: ["https://www.chess.com/terms/discovered-check-chess"],
      workbookRef: "workbook-101",
      difficultyLevel: "Beginner",
      status: "Published",
    },
  ],
  [
    "discovered-attack.pgn",
    {
      title: "Discovered Attack",
      description: "Master the art of discovered attacks",
      isPublic: true,
      ownerId: null,
      tags: ["tactics", "discovered attack", "intermediate"],
      urls: ["https://www.chess.com/terms/discovered-attack-chess"],
      workbookRef: "workbook-102",
      difficultyLevel: "Intermediate",
    },
  ],
  [
    "smothered-mate.pgn",
    {
      title: "Smothered Mate",
      description: "The beautiful knight mate pattern",
      isPublic: true,
      ownerId: null,
      tags: ["tactics", "mate", "knight", "philidor"],
      urls: ["https://www.chess.com/terms/smothered-mate-chess"],
      workbookRef: "workbook-103",
      difficultyLevel: "Intermediate",
    },
  ],
  [
    "Zugzvang-easy-to-hard.pgn",
    {
      title: "Zugzwang Easy to Hard",
      description: "Progressive exercises on zugzwang positions",
      isPublic: true,
      ownerId: null,
      tags: ["endgame", "zugzwang", "strategy"],
      urls: ["https://www.chess.com/terms/zugzwang-chess"],
      workbookRef: "workbook-104",
      difficultyLevel: "Advanced",
    },
  ],
]);

async function initializeTopic(fileName) {
  const topicData = MOCK_TOPICS.get(fileName);
  if (!topicData) {
    throw new Error(`No topic data found for ${fileName}`);
  }

  return TopicsService.createTopic(topicData);
}

async function initializeScenarios(fileName, topicId) {
  try {
    // Use the public path with process.env.BASE_URL
    const response = await fetch(
      `${process.env.BASE_URL}lessons/examples/${fileName}`
    );
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const pgn = await response.text();
    if (!pgn.trim()) return;

    const result = await TopicsService.importScenarios(topicId, pgn);
    return result.created;
  } catch (error) {
    console.error(`Failed to load scenarios for ${fileName}:`, error);
    return [];
  }
}

export async function initializeContent() {
  for (const fileName of MOCK_TOPICS.keys()) {
    const topic = await initializeTopic(fileName);
    await initializeScenarios(fileName, topic.id);
  }
}

if (useMockData) {
  // Initialize mock data
  initializeContent().then(() => {
    // Create mock curriculum after initialization
    TopicsService.getTopics().then((topics) => {
      const topicIds = topics.map((topic) => topic.id);

      CurriculumService.createCurriculum({
        title: "Basic Chess Tactics",
        description: "Learn fundamental chess tactics and patterns",
        status: "Published",
        lessons: topicIds
          .slice(0, 3)
          .map((id) => ({ topicId: id, isOptional: false })),
        modules: [
          { topicIds: topicIds.slice(0, 2) },
          { topicIds: [] },
          { topicIds: topicIds.slice(2, 3) },
          { topicIds: [] },
          { topicIds: [] },
          { topicIds: [] },
          { topicIds: [] },
          { topicIds: [] },
        ],
      }).then((curriculum) => {
        console.log("Mock curriculum created:", curriculum);
      });
    });
  });
}
